/* eslint-disable react/no-access-state-in-setstate */
// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import { Range } from 'react-input-range';

import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { removeTab } from 'wdna-components/lib/tabs';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import Loader from 'wdna-components/lib/loaders/local/Loader';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { messageError, messageInfo } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { parameter, ParameterDO, typeSensorVariable, TypeSensorVariableListDO, variable, VariableDO } from '../../../../configuration/requests';
import { sensor, SensorListDO } from '../../../../sensors/requests';
import { alarmsAction, ActionListDO, alarm, AlarmDO } from '../../../requests';

import Form from './Form';
import ButtonsForm from '../ButtonsForm';

const MAX_VAL: number = 3600 * 24;

type CustomAlarmDO = Partial<AlarmDO> & {
  checkboxMin?: boolean;
  checkboxMax?: boolean;
}

interface Props {
  rowId?: number;
  notify: () => void;
  duplicate: boolean;
}

interface State {
  loading: boolean;

  sensorArray: SensorListDO[];
  typeVariableArray: TypeSensorVariableListDO[];
  variableArray: VariableDO[];
  actionArray: ActionListDO[];

  typeAlarm: boolean;
  typeAction: string;
  previewText?: string;

  defEmailSubject?: ParameterDO;
  defNcxSubject?: ParameterDO;
  defSmsBody?: ParameterDO;
  defNcxBody?: ParameterDO;
  defVoiceBody?: ParameterDO;
  defEmailBody?: ParameterDO;

  timetableL1: Range;
  timetableM1: Range;
  timetableX1: Range;
  timetableJ1: Range;
  timetableV1: Range;
  timetableS1: Range;
  timetableD1: Range;
  timetableL2: Range;
  timetableM2: Range;
  timetableX2: Range;
  timetableJ2: Range;
  timetableV2: Range;
  timetableS2: Range;
  timetableD2: Range;

  data: CustomAlarmDO;
}

export default class Edit extends React.Component<Props, State> {
  private pid: string;
  private defaultValues: any;
  private mounted: boolean

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.defaultValues = {};

    this.state = {
      loading: false,

      sensorArray: [],
      typeVariableArray: [],
      variableArray: [],
      actionArray: [],

      typeAlarm: false,
      typeAction: '',
      previewText: '',

      timetableL1: { min: 0, max: 0 },
      timetableM1: { min: 0, max: 0 },
      timetableX1: { min: 0, max: 0 },
      timetableJ1: { min: 0, max: 0 },
      timetableV1: { min: 0, max: 0 },
      timetableS1: { min: 0, max: 0 },
      timetableD1: { min: 0, max: 0 },
      timetableL2: { min: 0, max: 0 },
      timetableM2: { min: 0, max: 0 },
      timetableX2: { min: 0, max: 0 },
      timetableJ2: { min: 0, max: 0 },
      timetableV2: { min: 0, max: 0 },
      timetableS2: { min: 0, max: 0 },
      timetableD2: { min: 0, max: 0 },

      data: {
        active: false,
        timetableList: [
          [0, MAX_VAL],
          [0, MAX_VAL],
          [0, MAX_VAL],
          [0, MAX_VAL],
          [0, MAX_VAL],
          [0, MAX_VAL],
          [0, MAX_VAL],
          [0, 0],
          [0, 0],
          [0, 0],
          [0, 0],
          [0, 0],
          [0, 0],
          [0, 0],
        ],
      }
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  };

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  private getRange(timeList: [number, number][] | undefined, idx: number): Range {
    const result: Range = { min: 0, max: 0 };

    if (timeList) {
      result.min = timeList[idx][0] / 3600;
      result.max = timeList[idx][1] / 3600;
    }

    return result;
  }

  public load = async (): Promise<void> => {
    const { rowId } = this.props || {};
    const isNew: boolean = (rowId === undefined || !!isNaN(rowId));

    this.loadingStart();
    try {
      const req: Record<string, any> = { alarm: true };

      const [sensorArray, actionArray, variableArray, defEmailSubject, defEmailBody, defSmsBody, defVoiceBody, defNcxSubject, defNcxBody] = await Promise.all([
        sensor<SensorListDO[]>('list', undefined, undefined, { params: { order: 'company' } }) || [],
        alarmsAction<ActionListDO[]>('list', undefined, undefined, { params: { order: 'name' } }) || [],
        variable<VariableDO[]>('list', undefined, req) || [],
        parameter<ParameterDO>('get', 'alarm_def_email_subject'),
        parameter<ParameterDO>('get', 'alarm_def_email_body'),
        parameter<ParameterDO>('get', 'alarm_def_sms_body'),
        parameter<ParameterDO>('get', 'alarm_def_voice_body'),
        parameter<ParameterDO>('get', 'alarm_ncx_email_subject'),
        parameter<ParameterDO>('get', 'alarm_ncx_email_body'),
      ]);

      let typeAlarm: boolean = false;
      let typeAction: string = '';
      let typeVariableArray: TypeSensorVariableListDO[] = [];

      let tsVariable: any = null;
      let rowData: Partial<AlarmDO> = this.state.data;

      if (!isNew) {
        rowData = await alarm<Partial<AlarmDO>>('get', String(rowId)) || rowData;
        tsVariable = rowData?.typeSensorVariable ? { id: rowData.typeSensorVariable.id } : null;
        typeAlarm = !!tsVariable;
        typeAction = rowData?.action?.type || '';
        // eslint-disable-next-line max-len
        typeVariableArray = await typeSensorVariable<TypeSensorVariableListDO[]>('list', undefined, { typeSensorId: rowData.sensor && rowData.sensor.typeSensor.id, alarm: true }) || [];
      }

      this.setState({
        data: {
          ...rowData,
          typeSensorVariable: tsVariable,
          checkboxMin: (rowData?.min != null),
          checkboxMax: (rowData?.max != null),
        },
        typeAlarm,
        typeAction,
        sensorArray,
        actionArray,
        typeVariableArray,
        variableArray,
        defEmailSubject,
        defEmailBody,
        defSmsBody,
        defVoiceBody,
        defNcxSubject,
        defNcxBody,
        timetableL1: this.getRange(rowData.timetableList, 0),
        timetableM1: this.getRange(rowData.timetableList, 0),
        timetableX1: this.getRange(rowData.timetableList, 0),
        timetableJ1: this.getRange(rowData.timetableList, 0),
        timetableV1: this.getRange(rowData.timetableList, 0),
        timetableS1: this.getRange(rowData.timetableList, 0),
        timetableD1: this.getRange(rowData.timetableList, 0),
        timetableL2: this.getRange(rowData.timetableList, 0),
        timetableM2: this.getRange(rowData.timetableList, 0),
        timetableX2: this.getRange(rowData.timetableList, 0),
        timetableJ2: this.getRange(rowData.timetableList, 0),
        timetableV2: this.getRange(rowData.timetableList, 0),
        timetableS2: this.getRange(rowData.timetableList, 0),
        timetableD2: this.getRange(rowData.timetableList, 13),
      }, () => {
        this.defaultValues = _.cloneDeep(rowData);
      });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setState({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setState({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  public onReset = (): void => {
    const rowData: CustomAlarmDO = this.state.data;
    this.setState({
      data: {
        checkboxMin: (rowData.min != null),
        checkboxMax: (rowData.max != null),
        ...this.defaultValues,
      }
    });
  };

  public handleClickRadio = (e: React.MouseEvent<HTMLInputElement, MouseEvent>): void => {
    const formField: ParentNode | null = (e.target as HTMLElement).parentNode;
    const { typeAlarm } = (formField?.children as any) || {};
    const affectedInputs: string[] = ['max', 'min', 'repeat', 'minutes'];
    const affectedInputsValues: Record<string, any> = {};

    // TODO: find a cleaner way to handle variables affected by radio button
    affectedInputs.forEach((key: string) => { affectedInputsValues[key] = typeAlarm.checked ? (document.getElementById(key) as HTMLInputElement)?.value : ''; });
    affectedInputsValues.typeSensorVariable = typeAlarm.checked ? { id: (document.getElementById('typeSensorVariable.id') as HTMLInputElement)?.value } : null;
    affectedInputsValues.disconnection = typeAlarm.checked ? '' : (document.getElementById('disconnection') as HTMLInputElement)?.value;
    this.setState((prevState: State) => ({
      data: {
        ...prevState.data,
        ...affectedInputsValues,
      },
      typeAlarm: typeAlarm.checked,
    }));
  };

  public handleClickPreview = (): void => {
    const { variableArray, typeVariableArray, sensorArray, typeAlarm, typeAction, data } = this.state;
    const notAbleToPreview: any = !data.sensor || !data.sensor.id || (typeAlarm && (!data.typeSensorVariable || !data.typeSensorVariable.id));
    if (notAbleToPreview) return;

    let messageBodyDef: string = '';
    let messageBodyName: string = '';
    switch (typeAction?.toUpperCase()) {
      case 'SMS':
        messageBodyDef = (typeAlarm ? 'defSmsBody' : 'defNcxBody');
        messageBodyName = 'smsBody';
        break;
      case 'VOICE':
        messageBodyDef = (typeAlarm ? 'defVoiceBody' : 'defNcxBody');
        messageBodyName = 'voiceBody';
        break;
      case 'EMAIL':
        messageBodyDef = (typeAlarm ? 'defEmailBody' : 'defNcxBody');
        messageBodyName = 'mailBody';
        break;
      default:
        messageBodyDef = '';
        messageBodyName = 'body';
        break;
    }

    const selectedSensorName: string = sensorArray?.find((item: SensorListDO) => item.id === +(data?.sensor?.id || 0))?.name || '';
    const selectedSensorDescription: string = sensorArray?.find((item: SensorListDO) => item.id === +(data?.sensor?.id || 0))?.description || '';
    const selectedSensorId: string = String(data?.sensor?.id || '');
    this.setState((prevState: State) => {
      // eslint-disable-next-line max-len
      const subject: string = (messageBodyName === 'mailBody' ? prevState.data.mailSubject || (typeAlarm ? prevState.defEmailSubject?.value : prevState.defNcxSubject?.value) : '') || '';
      let body: string = prevState.data[messageBodyName] || prevState[messageBodyDef].value;

      if (subject) {
        body = `${subject}\n\n${body}`;
      }

      body = body.replace(/{sensor}/g, selectedSensorId);
      body = body.replace(/{description}/g, selectedSensorDescription);
      body = body.replace(/{name}/g, selectedSensorName);
      if (prevState.typeAlarm) {
        const typeVariableChosen: TypeSensorVariableListDO | undefined = typeVariableArray
          ?.find((variableType: TypeSensorVariableListDO) => variableType.id === +(data?.typeSensorVariable?.id || 0));
        const variableChosen: VariableDO | undefined = variableArray?.find((variableName: VariableDO) => variableName.id === +(typeVariableChosen?.variableId || 0));
        body = body.replace(/{variable}/g, variableChosen?.name || '');
        body = body.replace(/{min}/g, String(prevState.data.min));
        body = body.replace(/{max}/g, String(prevState.data.max));
        body = body.replace(/{unit}/g, variableChosen?.unit || '');
      }

      return { previewText: body };
    });
  };

  public handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = e.target;
    const [outerName, innerName] = name.split('.');
    this.setState((prevState: State) => ({
      data: {
        ...prevState.data,
        [outerName]: {
          [innerName]: value,
        },
      },
    }));
  };

  public handleChangeSelectReset = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = e.target;
    const { actionArray } = this.state;

    const [outerName, innerName] = name.split('.');
    const ac: Partial<ActionListDO> | undefined = (
      (outerName === 'action' && value) ? actionArray?.find((item: ActionListDO) => item.id === +value) : { type: this.state.typeAction }
    );

    if (ac?.name === this.state.typeAction) {
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          [outerName]: {
            [innerName]: value,
          },
        },
        typeAction: ac?.type || '',
      }));
    } else {
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          [outerName]: {
            [innerName]: value,
          },
          mailTo: '',
          msgUnique: false,
          smsTo: '',
          voiceTo: '',
          launch: undefined,
        },
        typeAction: ac?.type || '',
      }));
    }
  };

  public handleChangeStandard = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    const splitName: string[] = name.split('.');
    if (splitName.length === 2) {
      const [outerName, innerName] = splitName;
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          [outerName]: {
            ...prevState.data[outerName],
            [innerName]: value,
          },
        }
      }));
    } else {
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          [name]: value,
        },
      }));
    }
  };

  public handleChangeDevice = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = e.target;
    if (value) {
      const { sensorArray } = this.state;
      const { typeSensorId } = sensorArray?.find((item: SensorListDO) => item.id === +value) || {};

      this.loadingStart();
      typeSensorVariable<TypeSensorVariableListDO[]>('list', undefined, { typeSensorId, alarm: true })
        ?.then((resp: any) => {
          const typeVariableArray: TypeSensorVariableListDO[] = resp;
          const [outerName, innerName] = name.split('.');
          this.setState((prevState: State) => ({
            data: {
              ...prevState.data,
              [outerName]: {
                [innerName]: value,
              },
            },
            typeVariableArray,
          }));
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    }
  };

  public handleChangeCheckbox = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, checked, dataset } = e.target;
    if (name === 'checkboxMin' || name === 'checkboxMax') {
      const input: string = (name === 'checkboxMin') ? 'min' : 'max';
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          [name]: checked,
          [input]: checked ? prevState.data[input] : '',
        },
      }));
    } else {
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          [name]: dataset.handlerType === 'integer' ? +checked : checked,
        },
      }));
    }
  };

  public submit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const { duplicate } = this.props;
    const { data, typeAlarm, typeAction } = this.state;

    if (duplicate) data.id = undefined;

    switch (typeAction?.toUpperCase()) {
      case 'SMS':
        if (!data.smsBody) data.smsBody = (typeAlarm ? this.state.defSmsBody?.value : this.state.defNcxBody?.value);
        break;
      case 'VOICE':
        if (!data.voiceBody) data.voiceBody = (typeAlarm ? this.state.defVoiceBody?.value : this.state.defNcxBody?.value);
        break;
      case 'EMAIL':
        if (!data.mailSubject) data.mailSubject = (typeAlarm ? this.state.defEmailSubject?.value : this.state.defNcxSubject?.value);
        if (!data.mailBody) data.mailBody = (typeAlarm ? this.state.defEmailBody?.value : this.state.defNcxBody?.value);
        break;
      default:
        break;
    }

    data.timetableList = this.formatTimetable();
    if (!this.isValidForm(data, typeAlarm, typeAction)) return;

    this.loadingStart();
    alarm('set', undefined, data)
      ?.then(() => {
        messageInfo('Success', `Alarm successfully ${data.id ? 'edited' : 'created'}`, 5000);
        const { notify } = this.props || {};
        if (typeof notify === 'function') {
          notify();
        }
        window.store.dispatch(removeTab('AE'));
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public isValidForm = (data: CustomAlarmDO, typeAlarm: boolean, typeAction: string): boolean => {
    const deviceSelected: number | undefined = data?.sensor?.id;
    if (!deviceSelected) {
      messageError('Error', 'Please select a device');
      return false;
    }

    const variableSelected: number | undefined = data?.typeSensorVariable?.id;
    if (typeAlarm && !variableSelected) {
      messageError('Error', 'Please select a variable');
      return false;
    }

    if (typeAlarm && !data.min && !data.max) {
      messageError('Error', 'Please select Min or Max value for the variable');
      return false;
    }

    const actionSelected: number | undefined = data.action && data.action.id;
    if (!actionSelected) {
      messageError('Error', 'Please select an action');
      return false;
    }

    const gatewaySelected: number | undefined = data.gatewayId;
    if (!gatewaySelected && typeAction.toUpperCase() === 'HARDWARE') {
      messageError('Error', 'Please select a gateway');
      return false;
    }

    const rexNumber: RegExp = /^[0-9]+$/i;
    const rexEmail: RegExp = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.([a-zA-Z]{2,4})+$/i;

    if (typeAction.toUpperCase() === 'EMAIL') {
      const { mailTo } = data;
      const mailList: string[] = mailTo?.split(';') || [];
      mailList.forEach((mail: string) => {
        const isValidEmail: boolean = rexEmail.test(mail);
        if (!isValidEmail) {
          messageError('Error', 'Please enter a valid email');
          return false;
        }
      });
    }
    if (typeAction.toUpperCase() === 'SMS') {
      const { smsTo } = data;
      const smsList: string[] = smsTo?.split(';') || [];
      smsList.forEach((sms: string) => {
        const isValidSms: boolean = rexNumber.test(sms);
        if (!isValidSms) {
          messageError('Error', 'Please enter a valid phone number');
          return false;
        }
      });
    }
    if (typeAction.toUpperCase() === 'VOICE') {
      const { voiceTo } = data;
      const voiceList: string[] = voiceTo?.split(';') || [];
      voiceList.forEach((voice: string) => {
        const isValidVoice: boolean = rexNumber.test(voice);
        if (!isValidVoice) {
          messageError('Error', 'Please enter a valid phone number');
          return false;
        }
      });
    }

    return true;
  };

  public formatTimetable = (): [number, number][] => {
    const fV: State = this.state;
    const timetableList: [number, number][] = [
      [(fV.timetableL1.min * 3600), (fV.timetableL1.max * 3600)],
      [(fV.timetableM1.min * 3600), (fV.timetableM1.max * 3600)],
      [(fV.timetableX1.min * 3600), (fV.timetableX1.max * 3600)],
      [(fV.timetableJ1.min * 3600), (fV.timetableJ1.max * 3600)],
      [(fV.timetableV1.min * 3600), (fV.timetableV1.max * 3600)],
      [(fV.timetableS1.min * 3600), (fV.timetableS1.max * 3600)],
      [(fV.timetableD1.min * 3600), (fV.timetableD1.max * 3600)],
      [(fV.timetableL2.min * 3600), (fV.timetableL2.max * 3600)],
      [(fV.timetableM2.min * 3600), (fV.timetableM2.max * 3600)],
      [(fV.timetableX2.min * 3600), (fV.timetableX2.max * 3600)],
      [(fV.timetableJ2.min * 3600), (fV.timetableJ2.max * 3600)],
      [(fV.timetableV2.min * 3600), (fV.timetableV2.max * 3600)],
      [(fV.timetableS2.min * 3600), (fV.timetableS2.max * 3600)],
      [(fV.timetableD2.min * 3600), (fV.timetableD2.max * 3600)],
    ];
    return timetableList;
  };

  public handleResetRange = (): void => {
    this.setState({
      timetableL1: { min: 0, max: 0 },
      timetableM1: { min: 0, max: 0 },
      timetableX1: { min: 0, max: 0 },
      timetableJ1: { min: 0, max: 0 },
      timetableV1: { min: 0, max: 0 },
      timetableS1: { min: 0, max: 0 },
      timetableD1: { min: 0, max: 0 },
    });
  };

  public handleResetRangeTwo = (): void => {
    this.setState({
      timetableL2: { min: 0, max: 0 },
      timetableM2: { min: 0, max: 0 },
      timetableX2: { min: 0, max: 0 },
      timetableJ2: { min: 0, max: 0 },
      timetableV2: { min: 0, max: 0 },
      timetableS2: { min: 0, max: 0 },
      timetableD2: { min: 0, max: 0 },
    });
  };

  public handleMaxRange = (): void => {
    this.setState({
      timetableL1: { min: 0, max: 24 },
      timetableM1: { min: 0, max: 24 },
      timetableX1: { min: 0, max: 24 },
      timetableJ1: { min: 0, max: 24 },
      timetableV1: { min: 0, max: 24 },
      timetableS1: { min: 0, max: 24 },
      timetableD1: { min: 0, max: 24 },
    });
  };

  public handleMaxRangeTwo = (): void => {
    this.setState({
      timetableL2: { min: 0, max: 24 },
      timetableM2: { min: 0, max: 24 },
      timetableX2: { min: 0, max: 24 },
      timetableJ2: { min: 0, max: 24 },
      timetableV2: { min: 0, max: 24 },
      timetableS2: { min: 0, max: 24 },
      timetableD2: { min: 0, max: 24 },
    });
  };

  public handleChangeRange = (name: string, value: number | Range): void => {
    this.setState((prevState: State) => ({
      ...prevState,
      [name]: value,
    }));
  };

  public render(): JSX.Element {
    const { loading, data } = this.state;

    return (
      <div id="content" className="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
                fullscreenbutton={false}
              >
                <div className="widget-body">
                  <Loader show={loading} overlay={false} />
                  <UiValidate>
                    <form className="smart-form" onSubmit={this.submit} method="post" encType="multipart/form-data">
                      <header>
                        <h2>
                          <span className="widget-icon" style={{ padding: '8px' }}>
                            <i className="fa fa-edit" />
                          </span>
                          Alarm {(data.id && !this.props.duplicate) ? `Edit: ${data.id}` : 'New'}
                        </h2>
                      </header>
                      <Form
                        formValues={this.state}
                        onChangeStandard={this.handleChangeStandard}
                        onChangeSelect={this.handleChangeSelect}
                        onChangeSelectReset={this.handleChangeSelectReset}
                        onChangeCheckbox={this.handleChangeCheckbox}
                        onChangeDevice={this.handleChangeDevice}
                        onClickRadio={this.handleClickRadio}
                        onClickPreview={this.handleClickPreview}
                        onChangeRange={this.handleChangeRange}
                        onResetRange={this.handleResetRange}
                        onResetRangeTwo={this.handleResetRangeTwo}
                        onMaxRange={this.handleMaxRange}
                        onMaxRangeTwo={this.handleMaxRangeTwo}
                      />
                      <ButtonsForm loading={loading} onReset={this.onReset} />
                    </form>
                  </UiValidate>
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
